import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Euro, Eye, FileText, Info, Receipt } from "lucide-react";
import { Fragment, useState } from "react";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, EmptyState, Skeleton, Tip } from "@/components/ui";
import { api, ApiError, type CustomerCosts } from "@/lib/api";
import { fmtNumber } from "@/lib/format";
import { intlLocale, t } from "@/lib/i18n";

const eur = (n: number | null | undefined, digits = 2) => (n == null ? "—" : n.toLocaleString(intlLocale(), { style: "currency", currency: "EUR", minimumFractionDigits: digits, maximumFractionDigits: digits }));
const dmy = (s: string | null) => (s ? new Date(`${s}T12:00:00Z`).toLocaleDateString(intlLocale()) : "—");
const monthLabel = (m: string) => new Date(`${m}-15T12:00:00Z`).toLocaleDateString(intlLocale(), { month: "short", year: "2-digit" });

/** Kostenübersicht (von Logiful je Kunde freigeschaltet) */
export function CostsPage() {
  const { tenantId, me } = useTenant();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["costs", tenantId], queryFn: () => api.costs(tenantId), staleTime: 5 * 60_000, retry: false });
  const [open, setOpen] = useState<number | null>(null);
  const d = q.data;
  const staffAdmin = me.user.staffRole === "staff_admin";

  if (q.isError)
    return (
      <div className="pb-16">
        <PageHeader title={t("Kosten")} />
        <div className="px-4 md:px-8"><Card className="p-5 text-[13px]">{q.error instanceof ApiError ? ((q.error.body as { message?: string })?.message ?? t("Nicht verfügbar")) : t("Nicht verfügbar")}</Card></div>
      </div>
    );
  if (d && !("customerNumber" in d))
    return (
      <div className="pb-16">
        <PageHeader title={t("Kosten")} />
        <EmptyState icon={<Euro className="size-5" />} title={t("Noch nicht freigeschaltet")}>{t("Die Kostenübersicht schaltet Logiful auf Wunsch für euch frei – sprecht uns gern an.")}</EmptyState>
      </div>
    );
  const c = d as CustomerCosts | undefined;
  const last = c?.invoices[0];
  const prev = c?.invoices[1];
  const maxMonth = Math.max(1, ...(c?.months ?? []).map((m) => m.net));
  const maxGroup = Math.max(1, ...(c?.groups12m ?? []).map((g) => g.net));
  const total12 = (c?.groups12m ?? []).reduce((s, g) => s + g.net, 0);

  return (
    <div className="pb-16">
      <PageHeader title={t("Kosten")} description={t("Eure Rechnungen bei Logiful – nachvollziehbar je Kostenart und je versendetem Auftrag. Alle Beträge netto.")} />
      <div className="px-4 md:px-8 space-y-4 max-w-5xl">
        {!c ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-28" />)}</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Card className="p-4">
                <p className="text-[12.5px] text-fg-muted flex items-center gap-1.5">{t("Laufender Zeitraum")} <Tip content={t("Seit {v}: bisher versendete Aufträge × Ø Kosten je Auftrag der letzten Rechnungen. Eine Schätzung – maßgeblich ist die Rechnung.", { v: dmy(c.current.since) })}><Info className="size-3 text-fg-subtle" /></Tip></p>
                <p className="mt-1 text-[24px] font-semibold tabular-nums">{c.current.estimate != null ? `≈ ${eur(c.current.estimate, 0)}` : "—"}</p>
                <p className="text-[12px] text-fg-subtle">{fmtNumber(c.current.shippedOrders)} {t("Aufträge seit")} {dmy(c.current.since)}{c.pending.net ? t(" · zzgl. {v} vorgemerkt", { v: eur(c.pending.net) }) : ""}</p>
              </Card>
              <Card className="p-4">
                <p className="text-[12.5px] text-fg-muted">{t("Letzte Rechnung")}</p>
                <p className="mt-1 text-[24px] font-semibold tabular-nums">{eur(last?.net)}</p>
                <p className="text-[12px] text-fg-subtle">{last ? `${dmy(last.periodStart)}–${dmy(last.periodEnd)}${prev ? ` · Vorrechnung ${eur(prev.net, 0)}` : ""}` : t("noch keine")}</p>
              </Card>
              <Card className="p-4">
                <p className="text-[12.5px] text-fg-muted">{t("Kosten je versendetem Auftrag")}</p>
                <p className="mt-1 text-[24px] font-semibold tabular-nums">{eur(last?.costPerOrder ?? c.current.avgCostPerOrder)}</p>
                <p className="text-[12px] text-fg-subtle">{c.current.avgCostPerOrder != null ? t("Ø letzte Rechnungen {v}", { v: eur(c.current.avgCostPerOrder) }) : t("Alle Kosten ÷ versendete Aufträge")}</p>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="p-5">
                <h2 className="font-semibold text-[14px]">{t("Je Monat")}</h2>
                <p className="text-[12px] text-fg-subtle mb-3">{t("netto · darunter Kosten je Auftrag")}</p>
                {c.months.length ? (
                  <div className="h-40 flex items-end gap-1.5">
                    {c.months.map((m) => (
                      <Tip key={m.month} content={t("{v}: {v2} · {v3} Aufträge{v4}", { v: monthLabel(m.month), v2: eur(m.net), v3: fmtNumber(m.shippedOrders), v4: m.shippedOrders ? t(" · {v} je Auftrag", { v: eur(m.net / m.shippedOrders) }) : "" })}>
                        <div className="flex-1 min-w-0 h-full flex flex-col justify-end items-center gap-1">
                          <div className="w-full rounded-t-[3px] bg-ink/70 hover:bg-ink" style={{ height: `${Math.max(3, (m.net / maxMonth) * 100)}%` }} />
                          <span className="text-[10.5px] text-fg-subtle whitespace-nowrap">{monthLabel(m.month)}</span>
                        </div>
                      </Tip>
                    ))}
                  </div>
                ) : <p className="text-[13px] text-fg-muted">{t("Noch keine Rechnungen.")}</p>}
              </Card>
              <Card className="p-5">
                <h2 className="font-semibold text-[14px]">{t("Kostenarten (12 Monate)")}</h2>
                <p className="text-[12px] text-fg-subtle mb-3">{t("Summe")} {eur(total12, 0)}</p>
                <ul className="space-y-2">
                  {c.groups12m.map((g) => (
                    <li key={g.group} className="text-[13px]">
                      <div className="flex items-baseline gap-2"><span>{g.group}</span><span className="ml-auto tabular-nums">{eur(g.net, 0)}<span className="text-fg-subtle"> · {total12 ? Math.round((g.net / total12) * 100) : 0} %</span></span></div>
                      <div className="mt-1 h-1.5 rounded-full bg-surface-2 overflow-hidden"><div className="h-full rounded-full bg-ink/70" style={{ width: `${(g.net / maxGroup) * 100}%` }} /></div>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>

            <Card className="overflow-hidden">
              <div className="px-5 pt-4 pb-3 flex items-center gap-2"><Receipt className="size-4 text-fg-subtle" /><h2 className="font-semibold text-[14px]">{t("Rechnungen")}</h2></div>
              {c.invoices.length === 0 ? <p className="px-5 pb-5 text-[13px] text-fg-muted">{t("Noch keine Rechnungen.")}</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[13px]">
                    <thead className="text-[12px] text-fg-subtle border-y border-border bg-surface-2/50">
                      <tr><th className="h-9 pl-5 pr-3 text-left font-medium">{t("Rechnung")}</th><th className="h-9 px-3 text-left font-medium">{t("Zeitraum")}</th><th className="h-9 px-3 text-right font-medium">{t("Netto")}</th><th className="h-9 px-3 text-right font-medium">{t("Aufträge")}</th><th className="h-9 px-3 text-right font-medium">{t("je Auftrag")}</th><th className="h-9 px-3 text-left font-medium">{t("Status")}</th><th className="h-9 pl-3 pr-5" /></tr>
                    </thead>
                    <tbody>
                      {c.invoices.map((i) => (
                        <Fragment key={i.id}>
                          <tr className="border-b border-border hover:bg-surface-2/50 cursor-pointer" onClick={() => setOpen(open === i.id ? null : i.id)}>
                            <td className="pl-5 pr-3 py-2.5"><span className="font-medium font-mono text-[12.5px]">{i.number ?? "—"}</span><span className="block text-[11.5px] text-fg-subtle">{dmy(i.date)}</span></td>
                            <td className="px-3 py-2.5 text-fg-muted whitespace-nowrap">{dmy(i.periodStart)}–{dmy(i.periodEnd)}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums font-medium">{eur(i.net)}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-fg-muted">{fmtNumber(i.shippedOrders)}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums">{eur(i.costPerOrder)}</td>
                            <td className="px-3 py-2.5">{i.paid ? <Badge tone="success">{t("bezahlt")}</Badge> : <Badge>{i.paymentMethod === "lastschrift" ? t("Lastschrift") : t("offen")}</Badge>}</td>
                            <td className="pl-3 pr-5 py-2.5 text-right"><ChevronDown className={cx("size-3.5 inline transition-transform text-fg-subtle", open === i.id && "rotate-180")} /></td>
                          </tr>
                          {open === i.id && (
                            <tr className="border-b border-border bg-surface-2/30">
                              <td colSpan={7} className="px-5 py-3">
                                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-1 text-[12.5px]">
                                  {i.groups.map((g) => <li key={g.group} className="flex"><span className="text-fg-muted">{g.group}</span><span className="ml-auto tabular-nums">{eur(g.net)}</span></li>)}
                                </ul>
                                <p className="mt-2 text-[12px] text-fg-subtle">{t("Brutto")} {eur(i.gross)}</p>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {c.pending.items.length > 0 && (
              <Card className="overflow-hidden">
                <div className="px-5 pt-4 pb-3 flex items-center gap-2"><FileText className="size-4 text-fg-subtle" /><h2 className="font-semibold text-[14px]">{t("Für die nächste Rechnung vorgemerkt")}</h2><span className="ml-auto tabular-nums text-[13px] font-medium">{eur(c.pending.net)}</span></div>
                <ul className="divide-y divide-border border-t border-border text-[13px]">
                  {c.pending.items.map((p, k) => (
                    <li key={k} className="px-5 py-2 flex flex-wrap items-center gap-3">
                      <span className="min-w-0 flex-1">{p.position ?? p.group}<span className="block text-[11.5px] text-fg-subtle">{p.group} · {dmy(p.date)}</span></span>
                      <span className="tabular-nums text-fg-muted">{fmtNumber(p.quantity)} {p.unit ?? ""} × {eur(p.unitPrice)}{p.discountPct ? ` − ${p.discountPct} %` : ""}</span>
                      <span className="tabular-nums font-medium w-24 text-right">{eur(p.net)}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
            <p className="text-[12px] text-fg-subtle">{t("Quelle: Logiful-Abrechnung (Kd-Nr.")} {c.customerNumber}{t("). Kosten je Auftrag = Rechnungsbetrag ÷ im Zeitraum versendete Aufträge (enthält auch Lagerung, Wareneingang u. a.). Maßgeblich ist die Rechnung.")}</p>
          </>
        )}
      </div>
    </div>
  );
}
